import { Box, Typography } from '@mui/material';
import { styled } from '@mui/styles';
import { IconCancel, IconCross, IconEdit, IconTrash } from 'assets/img';
import { useCompanyRelativePath, useIsOgpPlannerOrHigher } from 'ogp/hooks';
import { useDeleteFlexPool } from 'ogp/hooks/queryHooks/flexPools/useDeleteFlexPool';
import { DeleteButton, PrimaryButton, useDialog } from 'ogp/providers/dialog-provider';
import { routes } from 'ogp/Routes';
import { useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { ContextMenuButton, ContextMenuItem, LwIconButton } from 'redesign';
import { flexPoolAlerts } from 'shared/components/alerts/alert-messages';
import { useAlert } from 'shared/components/alerts';
import { useQueryClient } from 'react-query';
import { QUERY_KEYS } from 'api/query-keys';
import { GetFlexPoolDetailResponse } from '../../../../../services/flex-pool-service.types';

type FlexPoolDetailHeadProps = {
  flexPool: GetFlexPoolDetailResponse['flexPoolDetail'];
  onClose: () => void;
};

export const FlexPoolDetailHead = ({ flexPool, onClose }: FlexPoolDetailHeadProps) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const updateFlexPoolPath = useUpdateFlexPoolPath();
  const isPlanner = useIsOgpPlannerOrHigher();
  const { alertSuccess, alertError } = useAlert();
  const generatePath = useCompanyRelativePath();
  const { openDialog, closeDialog, enableDialog, disableDialog } = useDialog();
  const deleteFlexPool = useDeleteFlexPool();

  if (!flexPool) {
    return null;
  }

  const handleDeleteFlexPool = (id: string) => {
    const flexWorkerCount = flexPool.flexWorkerCount;
    openDialog(
      {
        title: 'Flexpool verwijderen',
        content: flexWorkerCount
          ? `Er zitten nog ${flexWorkerCount} flexwerkers in deze flexpool, weet je zeker dat je deze flexpool wilt verwijderen?`
          : 'Weet je zeker dat je deze flexpool wilt verwijderen?',
        secondaryButton: (props) => (
          <PrimaryButton iconColorMode="fill" startIcon={<IconCancel />} {...props}>
            Annuleer
          </PrimaryButton>
        ),
        primaryButton: (props) => <DeleteButton {...props} />,
      },
      () => {
        disableDialog();
        deleteFlexPool.mutate(id, {
          onSuccess: () => {
            closeDialog();
            alertSuccess(flexPoolAlerts.success.deleted);
            navigate(generatePath(routes.FlexPools));
            queryClient.invalidateQueries(QUERY_KEYS.flexPools());
            queryClient.invalidateQueries(QUERY_KEYS.workers());
          },
          onError: (e) => alertError(e),
          onSettled: () => {
            enableDialog();
          },
        });
      }
    );
  };

  return (
    <Box display="flex" flexDirection="row">
      <Box display="flex" flexDirection="column">
        <Typography color="secondary" variant="body1" style={{ fontWeight: 'bold' }}>
          {flexPool.companyName}
        </Typography>
        <JobNameTitle variant="h5">{flexPool.name}</JobNameTitle>
      </Box>
      <Box display="flex" flexDirection="row" ml="auto" alignItems="middle" style={{ gap: '32px' }}>
        <ContextMenuButton size="medium" color="secondary">
          <ContextMenuItem
            disabled={!isPlanner}
            onClick={() => navigate(updateFlexPoolPath(flexPool.id))}
          >
            <IconEdit />
            Bewerken
          </ContextMenuItem>
          <ContextMenuItem disabled={!isPlanner} onClick={() => handleDeleteFlexPool(flexPool.id)}>
            <IconTrash />
            Verwijderen
          </ContextMenuItem>
        </ContextMenuButton>
        <LwIconButton iconColorMode="stroke" color="secondary" onClick={onClose}>
          <IconCross width={24} height={24} />
        </LwIconButton>
      </Box>
    </Box>
  );
};

function useUpdateFlexPoolPath() {
  const location = useLocation();
  const generatePath = useCompanyRelativePath();

  return useCallback(
    (flexPoolDetailId: string) => {
      const path = generatePath(routes.FlexPoolEdit);
      const params = new URLSearchParams(location.search);
      params.set('flexPoolID', flexPoolDetailId);
      return `${path}?${params}`;
    },
    [generatePath, location.search]
  );
}

const JobNameTitle = styled(Typography)(({ theme }) => ({
  marginTop: theme.spacing(2),
}));
